import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../base.service';
import { CommonUtility } from '../utility';
import { SPECTRUM_AUTH_API } from '../tokens';
import {
    SpectrumLoginRequest, SpectrumLoginResponse, SpectrumSchoolAccess
} from './login.model';

/**
 * Calls behind the Spectrum sign-in screen. Every path is derived from
 * SPECTRUM_AUTH_API so an app only has to provide that one token.
 *
 * The paths and parameter styles here mirror the auth API exactly as the
 * v1 portal calls it — several of them are irregular (OTP is verified via
 * `validateOtp` with the code sent as `password`, and the reset module name
 * carries a typo the API itself expects), so they are not tidied.
 */
@Injectable({ providedIn: 'root' })
export class SpectrumLoginService {

    private base = inject(BaseService);
    private authBase = inject(SPECTRUM_AUTH_API);

    /** Staff platform. Parent/student access is looked up under `SmartParent`. */
    private static readonly APP_NAME = 'Spectrum';
    private static readonly PARENT_APP_NAME = 'SmartParent';

    private get auth(): string {
        return `${this.authBase}/auth`;
    }

    /** Password sign-in. The auth API expects form-encoded parameters, not JSON. */
    login(credentials: SpectrumLoginRequest): Observable<SpectrumLoginResponse> {
        return this.base.post<SpectrumLoginResponse>(
            `${this.auth}/login`, CommonUtility.convertObjectToUrlEncoded({ ...credentials }));
    }

    /** Schools this account may sign in to as staff. */
    schoolAccess(username: string): Observable<SpectrumSchoolAccess[]> {
        return this.base.get<SpectrumSchoolAccess[]>(
            `${this.auth}/user-access?username=${encodeURIComponent(username)}`
            + `&appName=${SpectrumLoginService.APP_NAME}`);
    }

    /** Children this account may open the parent portal for. */
    parentAccess(username: string): Observable<SpectrumSchoolAccess[]> {
        return this.base.get<SpectrumSchoolAccess[]>(
            `${this.auth}/user-access?username=${encodeURIComponent(username)}`
            + `&appName=${SpectrumLoginService.PARENT_APP_NAME}`);
    }

    /** Sends the code. The API takes the number as `mobile` on the query string. */
    sendOtp(mobile: string): Observable<unknown> {
        return this.base.post(`${this.auth}/otp/send?mobile=${encodeURIComponent(mobile)}`, null);
    }

    /**
     * Verifies the code. The endpoint is `validateOtp`, and the code travels in
     * the `password` parameter — both are the API's spelling, not a mistake here.
     */
    verifyOtp(username: string, otp: string): Observable<SpectrumLoginResponse> {
        return this.base.post<SpectrumLoginResponse>(
            `${this.auth}/validateOtp?password=${encodeURIComponent(otp)}`
            + `&username=${encodeURIComponent(username)}`, null);
    }

    /** Used when login reports the password has expired. */
    changePassword(username: string, newPassword: string): Observable<SpectrumLoginResponse> {
        return this.base.post<SpectrumLoginResponse>(
            `${this.auth}/login-account/changePassword?username=${encodeURIComponent(username)}`
            + `&password=${encodeURIComponent(newPassword)}`, null);
    }

    /**
     * Starts a password reset by texting a code. `RESET_PASSSWORD` is spelled
     * with three S's by the auth API; correcting it here breaks the call.
     */
    sendResetCode(email: string): Observable<unknown> {
        return this.base.post(
            `${this.auth}/otp/send?email=${encodeURIComponent(email)}&otpModule=RESET_PASSSWORD`, null);
    }
}
